import { InlineAudioPlayer } from '@core/components';
import type { ClassificationDialogData, ClassificationContentType } from '@core/domain';
import { useSystemConfig } from '@core/hooks/useSystemConfig';
import { getContentDisplayText, formatTimestamp, getLanguageName } from '@core/utils';
import { Badge, BadgeSize, IconButton, Eye, IconButtonSize, IconSize, StatusCircleBlock } from '@soteria/design-system-ve';
import { useState } from 'react';

import * as styles from './ClassificationDialogContent.css';
import { LabelsSection } from './ClassificationDialogLabels';

interface ClassificationDialogContentProps {
  data: ClassificationDialogData;
  isContentBlurred: boolean;
  onToggleBlur: () => void;
}

/** Get content type display name */
function getContentTypeDisplay(contentType: ClassificationContentType): string {
  return contentType || 'unknown';
}

/** Get classification result display */
function getClassificationDisplay(isSafe: boolean): string {
  return isSafe ? 'allowed' : 'rejected';
}

/** Format confidence score */
function formatConfidence(score: number | undefined, contentType: ClassificationContentType): string {
  if (score === undefined) return 'N/A';
  // Image confidence is already percentage, text is decimal
  const percentage = contentType === 'image' ? score : score * 100;

  return `${percentage.toFixed(2)}%`;
}

/** Format processing time */
function formatProcessingTime(duration: number | undefined): string {
  if (duration === undefined) return 'N/A';

  return `${duration}ms`;
}

/** Metadata Row Component */
function MetadataRow({ label, value }: { label: string; value: string | React.ReactNode }) {
  return (
    <div className={styles.metadataRow}>
      <span className={styles.metadataRowLabel}>{label}:</span>
      <span className={styles.metadataRowValue}>{value}</span>
    </div>
  );
}

/** Content Information Column */
function ContentInformationColumn({ data }: { data: ClassificationDialogData }) {
  return (
    <div className={styles.metadataColumn}>
      <h4 className={styles.metadataColumnTitle}>Content Information</h4>
      <MetadataRow label="Subject ID" value={data.id || 'Unknown'} />
      <MetadataRow label="Timestamp (UTC)" value={formatTimestamp(data.timestamp)} />
      <MetadataRow label="Event Type" value={data.eventType || 'Classification'} />
      <MetadataRow label="Content Type" value={getContentTypeDisplay(data.contentType)} />
      <MetadataRow label="Topic Name" value={data.topicName || 'N/A'} />
      {data.contentUrl && (
        <div className={styles.metadataRow}>
          <span className={styles.metadataRowLabel}>Content URL:</span>
          <a className={styles.metadataRowValueLink} href={data.contentUrl} rel="noopener noreferrer" target="_blank" title={data.contentUrl}>
            {data.contentUrl.length > 60 ? `${data.contentUrl.substring(0, 60)}...` : data.contentUrl}
          </a>
        </div>
      )}
    </div>
  );
}

/** Classification Results Column */
function ClassificationResultsColumn({ data }: { data: ClassificationDialogData }) {
  return (
    <div className={styles.metadataColumn}>
      <h4 className={styles.metadataColumnTitle}>Classification Results</h4>
      <MetadataRow label="Confidence" value={formatConfidence(data.confidenceScore, data.contentType)} />
      <MetadataRow label="Classification" value={getClassificationDisplay(data.isSafe)} />
      <MetadataRow label="Processing Time" value={formatProcessingTime(data.duration)} />
      <MetadataRow label="Language" value={data.language ? getLanguageName(data.language) : 'N/A'} />
    </div>
  );
}

/** Policy Information Column */
function PolicyInformationColumn({ data }: { data: ClassificationDialogData }) {
  const triggeredPolicies = data.triggeredPolicies || [];
  const nonTriggeredPolicies = data.nonTriggeredPolicies || [];
  const totalPolicies = triggeredPolicies.length + nonTriggeredPolicies.length;
  const isCompliant = triggeredPolicies.length === 0;

  return (
    <div className={styles.metadataColumn}>
      <h4 className={styles.metadataColumnTitle}>Policy Information</h4>

      {totalPolicies > 0 && (
        <>
          <div className={styles.policyColumnBadgeRow}>
            <Badge size={BadgeSize.Small} text={isCompliant ? 'ALLOWED' : 'REJECTED'} />
            <span className={styles.policyViolationCount}>
              ({triggeredPolicies.length}/{totalPolicies}) Violations
            </span>
          </div>

          {triggeredPolicies.length > 0 && (
            <>
              <span className={styles.policyColumnSubtitle}>Triggered Policies ({triggeredPolicies.length})</span>
              <div className={styles.policyColumnList}>
                {triggeredPolicies.slice(0, 6).map(policy => (
                  <Badge key={policy.id} size={BadgeSize.Small} text={policy.name} title={policy.description} />
                ))}
                {triggeredPolicies.length > 6 && <span className={styles.morePoliciesText}>+ {triggeredPolicies.length - 6} more</span>}
              </div>
            </>
          )}

          {nonTriggeredPolicies.length > 0 && (
            <>
              <span className={styles.policyColumnSubtitle}>Nontriggered Policies ({nonTriggeredPolicies.length})</span>
              <div className={styles.policyColumnList}>
                {nonTriggeredPolicies.slice(0, 4).map(policy => (
                  <Badge key={policy.id} size={BadgeSize.Small} text={policy.name} title={policy.description} />
                ))}
                {nonTriggeredPolicies.length > 4 && <span className={styles.morePoliciesText}>+ {nonTriggeredPolicies.length - 4} more</span>}
              </div>
            </>
          )}
        </>
      )}

      {totalPolicies === 0 && <MetadataRow label="Status" value="No policies configured" />}
    </div>
  );
}

/** User Information Column */
function UserInformationColumn({ data }: { data: ClassificationDialogData }) {
  const userInfo = data.userInfo;

  return (
    <div className={styles.metadataColumn}>
      <h4 className={styles.metadataColumnTitle}>User Information</h4>
      <MetadataRow label="Type" value={userInfo?.type || 'N/A'} />
      <MetadataRow label="User ID" value={userInfo?.userId || 'N/A'} />
      <MetadataRow label="Username" value={userInfo?.username || 'N/A'} />
    </div>
  );
}

/** Format a custom value for display — primitives as-is, objects/arrays JSON-stringified */
function formatCustomValue(value: unknown): string {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'string') return value || 'N/A';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  return JSON.stringify(value);
}

/** Metadata Column */
function MetadataColumn({ data }: { data: ClassificationDialogData }) {
  const hasMetadata = data.metadata && Object.keys(data.metadata).length > 0;
  const hasCustomValues = data.customValues && Object.keys(data.customValues).length > 0;

  return (
    <div className={styles.metadataColumn}>
      <h4 className={styles.metadataColumnTitle}>Metadata</h4>

      {/* Raw metadata key-value pairs (server, world, etc.) */}
      {hasMetadata ? (
        Object.entries(data.metadata ?? {}).map(([key, value]) => <MetadataRow key={`meta-${key}`} label={key} value={value || 'N/A'} />)
      ) : (
        <span className={styles.metadataRowValue}>N/A</span>
      )}

      {/* Custom Values sub-section */}
      <h4 className={styles.metadataColumnTitle}>Custom Values</h4>
      {hasCustomValues ? (
        Object.entries(data.customValues ?? {}).map(([key, value]) => <MetadataRow key={key} label={key} value={formatCustomValue(value)} />)
      ) : (
        <span className={styles.metadataRowValue}>N/A</span>
      )}
    </div>
  );
}

/** Get content display information */
function getContentDisplay(data: ClassificationDialogData) {
  const content = getContentDisplayText(data.text, data.contentUrl);

  if (data.contentType === 'audio' && data.text) {
    return { label: 'Transcript', content };
  }

  if (data.contentType === 'text' && data.text) {
    return { label: 'Text', content };
  }

  if (data.contentType === 'username' && data.text) {
    return { label: 'Username', content };
  }

  if (data.contentType === 'image') {
    return { label: 'Content Text', content: data.text };
  }

  return null;
}

/** Text Content Section */
function TextContentSection({ data, isContentBlurred, onToggleBlur }: { data: ClassificationDialogData; isContentBlurred: boolean; onToggleBlur: () => void }) {
  const contentDisplay = getContentDisplay(data);
  const { mediaServiceUrl } = useSystemConfig();
  const isAudio = data.contentType === 'audio' && Boolean(data.contentUrl);

  if (!contentDisplay?.content) return null;

  const applyBlur = !data.isSafe && isContentBlurred;
  const toggleLabel = isContentBlurred ? 'Show content' : 'Hide content';

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>{contentDisplay.label}</h3>
      {isAudio && (
        <div className={styles.audioPlayerSection}>
          <InlineAudioPlayer audioId={data.id} audioUrl={data.contentUrl as string} mediaServiceUrl={mediaServiceUrl} />
        </div>
      )}
      <div className={styles.contentBoxContainer}>
        <div className={`${styles.contentBox} ${applyBlur ? styles.blurredText : ''}`}>{contentDisplay.content}</div>
        {!data.isSafe && (
          <IconButton
            aria-label={toggleLabel}
            buttonSize={IconButtonSize.Small}
            className={styles.hoverButton}
            icon={isContentBlurred ? Eye : StatusCircleBlock}
            iconSize={IconSize.Small}
            title={toggleLabel}
            onClick={onToggleBlur}
          />
        )}
      </div>
    </div>
  );
}

/** Wide Image Display Section */
function WideImageDisplaySection({
  data,
  isContentBlurred,
  onToggleBlur,
}: {
  data: ClassificationDialogData;
  isContentBlurred: boolean;
  onToggleBlur: () => void;
}) {
  const [imageError, setImageError] = useState(false);

  // Only show for image content with a URL
  if (data.contentType !== 'image' || !data.contentUrl) return null;

  const applyBlur = !data.isSafe && isContentBlurred;

  if (imageError) {
    return (
      <div className={styles.wideImageSection}>
        <div className={styles.imageErrorContainer}>
          <p className={styles.imageErrorText}>Unable to load image</p>
          <p className={styles.imageUrlText}>{data.contentUrl}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wideImageSection}>
      <div className={styles.wideImageContainer}>
        <img
          alt="Classification content"
          className={`${styles.wideFullImage} ${applyBlur ? styles.blurredImage : ''}`}
          src={data.contentUrl}
          onError={() => setImageError(true)}
        />
        {!data.isSafe && (
          <IconButton
            aria-label={isContentBlurred ? 'Show content' : 'Hide content'}
            buttonSize={IconButtonSize.Small}
            className={styles.visibleButton}
            icon={isContentBlurred ? Eye : StatusCircleBlock}
            iconSize={IconSize.Small}
            title={isContentBlurred ? 'Show content' : 'Hide content'}
            onClick={onToggleBlur}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Wide Layout Content for Classification Dialog
 */
export function ClassificationDialogContent({ data, isContentBlurred, onToggleBlur }: ClassificationDialogContentProps) {
  return (
    <div className={styles.container}>
      {/* Wide Image Display (full width at top for image content) */}
      <WideImageDisplaySection data={data} isContentBlurred={isContentBlurred} onToggleBlur={onToggleBlur} />

      {/* Text/Transcript Content Section */}
      <TextContentSection data={data} isContentBlurred={isContentBlurred} onToggleBlur={onToggleBlur} />

      {/* Five-column metadata grid */}
      <div className={styles.metadataColumnsGrid}>
        <ContentInformationColumn data={data} />
        <ClassificationResultsColumn data={data} />
        <PolicyInformationColumn data={data} />
        <UserInformationColumn data={data} />
        <MetadataColumn data={data} />
      </div>

      {/* Labels Section at bottom */}
      <LabelsSection data={data} />
    </div>
  );
}
